import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";

interface AdminRouteProps {
  children: React.ReactNode;
  /** When set, allow any of these roles (e.g. ["admin", "manager"] for user management). Default: ["admin"] only. */
  allowedRoles?: string[];
}

/**
 * Restricts access by role. Default: admin only.
 * Use allowedRoles={["admin", "manager"]} for routes that allow both (e.g. Users – Story 6-6-4).
 */
export function AdminRoute({ children, allowedRoles = ["admin"] }: AdminRouteProps) {
  const { user, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div
          className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  const allowed = allowedRoles.includes(user?.role ?? "");

  // Staff redirect only if they're NOT in allowedRoles (Story 10-5-8: Staff can now access operations view)
  if (user?.role === "staff" && !allowed) {
    return <Navigate to="/staff" state={{ from: location, accessDenied: true }} replace />;
  }

  if (!user || !allowed) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
